import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  SparklesIcon,
  ShieldCheckIcon,
  BoltIcon,
  AcademicCapIcon,
  ArrowRightIcon,
  RocketLaunchIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  CpuChipIcon,
  GlobeAltIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

const About = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.pageYOffset / totalHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const stats = [
    { number: "24/7", label: "Smart Evaluation", icon: <BoltIcon className="h-6 w-6" /> },
    { number: "100%", label: "Fairness Guarantee", icon: <ShieldCheckIcon className="h-6 w-6" /> },
    { number: "Real-time", label: "Live Leaderboards", icon: <ArrowTrendingUpIcon className="h-6 w-6" /> },
    { number: "Expert", label: "Curated Challenges", icon: <AcademicCapIcon className="h-6 w-6" /> }
  ];

  const fadeIn = {
    initial: { opacity: 0, y: 25 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 selection:bg-indigo-500/30 overflow-x-hidden">

      {/* Scroll Indicator */}
      <div className="fixed top-0 left-0 w-full h-[2px] z-50">
        <motion.div
          className="h-full bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* HERO */}
      <section className="relative py-28 px-6">
        <div className="absolute inset-0 bg-indigo-500/5 blur-[120px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto text-center">
          <motion.div {...fadeIn}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 mb-8">
              <SparklesIcon className="h-4 w-4 text-indigo-400" />
              <span className="text-xs font-semibold text-indigo-300 uppercase tracking-wider">
                Redefining Competitive Evaluation
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight tracking-tight mb-6">
              Where <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">Skill</span> 
              <br className="hidden md:block" /> Outweighs Credentials
            </h1>

            <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
              We are building a transparent merit-driven arena where real ability
              is evaluated through structured challenges and expert validation.
            </p>
          </motion.div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                {...fadeIn}
                transition={{ delay: i * 0.1 }}
                className="bg-white/[0.03] border border-white/5 rounded-2xl p-6 text-center"
              >
                <div className="text-indigo-400 mb-3 flex justify-center">
                  {stat.icon}
                </div>
                <div className="text-2xl font-bold">{stat.number}</div>
                <div className="text-xs uppercase tracking-widest text-gray-500 mt-1">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">

          <motion.div {...fadeIn}>
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-10 rounded-[2rem] shadow-xl shadow-indigo-500/10">
              <RocketLaunchIcon className="h-14 w-14 text-white mb-6" />
              <h3 className="text-2xl font-bold mb-4 uppercase tracking-wide">
                Our Mission
              </h3>
              <p className="text-blue-100/80 leading-relaxed mb-6">
                Identify and elevate the top 100 Team Leaders who demonstrate
                measurable excellence in competitive environments.
              </p>

              <ul className="space-y-3 text-sm">
                {["Blind Meritocracy", "Expert Validation", "Industry Alignment"].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircleIcon className="h-4 w-4 text-blue-200" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          <motion.div {...fadeIn}>
            <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
              Closing the <span className="text-indigo-500">Trust Gap</span>
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              In a digital era filled with automated credentials, we validate
              practical competence through rigorous evaluation systems that
              prioritize execution over appearance.
            </p>

            <Link
              to="/register"
              className="inline-flex items-center gap-2 font-semibold text-indigo-400 hover:text-indigo-300 transition"
            >
              Become a Founding Member
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </motion.div>

        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24 px-6 bg-black/40 border-t border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">

          <FeatureCard
            icon={<CpuChipIcon className="h-6 w-6" />}
            title="AI-Enhanced Logic"
            desc="Algorithmic fairness and structured scoring mechanisms."
          />

          <FeatureCard
            icon={<GlobeAltIcon className="h-6 w-6" />}
            title="Global Standards"
            desc="Challenges aligned with real-world industry benchmarks."
          />

          <FeatureCard
            icon={<BeakerIcon className="h-6 w-6" />}
            title="Continuous Evolution"
            desc="Community-driven improvements and feature refinement."
          />

        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center border-t border-white/5">
        <motion.div {...fadeIn}>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-8 tracking-tight">
            Join the <span className="text-indigo-500">Early Builders</span>
          </h2>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="px-10 py-4 bg-white text-black font-bold rounded-xl hover:bg-indigo-600 hover:text-white transition uppercase text-xs tracking-widest"
            >
              Join Now
            </Link>

            <Link
              to="/competitions"
              className="px-10 py-4 border border-white/10 rounded-xl hover:bg-white/5 transition uppercase text-xs tracking-widest"
            >
              Explore Challenges
            </Link>
          </div>

          <p className="mt-8 text-gray-600 text-xs uppercase tracking-widest">
            Limited early access to maintain evaluation quality.
          </p>
        </motion.div>
      </section>
    </div>
  );
};

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-[#0a0a0a] border border-white/5 rounded-2xl p-8 hover:border-indigo-500/30 transition">
    <div className="w-12 h-12 rounded-xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center mb-6">
      {icon}
    </div>
    <h3 className="text-lg font-semibold mb-3 uppercase tracking-wide">
      {title}
    </h3>
    <p className="text-gray-500 text-sm leading-relaxed">
      {desc}
    </p>
  </div>
);

export default About;